import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import ChartContainer from '../components/common/ChartContainer';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

const STATE_YOY_METRIC_LABELS = {
  evPct: 'EV%',
  icePct: 'ICE%',
  evPen: 'EV Penetration rate',
  icePen: 'ICE Penetration rate',
};

const DIM_META = {
  state: { label: 'State', titlePlural: 'States' },
  city: { label: 'City', titlePlural: 'Cities' },
  rto: { label: 'RTO', titlePlural: 'RTOs' },
};

const PenetrationAnalytics = () => {
  const theme = useTheme();
  const contentRef = useRef(null);
  const [stateRows, setStateRows] = useState([]);
  const [cityRows, setCityRows] = useState([]);
  const [rtoRows, setRtoRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stateYoYMetric, setStateYoYMetric] = useState('evPct');

  const toNum = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const toNumOrNull = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        const [st, ct, rt] = await Promise.all([
          axios.get('/api/penetration/state', { params: { limit: 50000 } }),
          axios.get('/api/penetration/city', { params: { limit: 50000 } }),
          axios.get('/api/penetration/rto', { params: { limit: 50000 } }),
        ]);
        setStateRows(Array.isArray(st.data) ? st.data : []);
        setCityRows(Array.isArray(ct.data) ? ct.data : []);
        setRtoRows(Array.isArray(rt.data) ? rt.data : []);
      } catch (e) {
        console.error('Error fetching penetration rows:', e);
        setStateRows([]); setCityRows([]); setRtoRows([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, []);

  const getLabel = (dim, r) => {
    if (!r) return '—';
    if (dim === 'state') return String(r.State || r.state || '—');
    if (dim === 'city') return String(r.City || r.city || r.District || r.district || '—');
    if (dim === 'rto') {
      const name = String(r.RTO || r.rto || '').trim();
      const code = String(r['RTO Code'] || r.rtoCode || '').trim();
      if (name && code) return `${name} (${code})`;
      if (name) return name;
      if (code) return code;
      // Fallbacks (some datasets may only have City/District)
      return String(r.City || r.city || r.District || r.district || r.State || r.state || '—');
    }
    return '—';
  };

  const getSeries = (r, key) => {
    if (!r) return {};
    const direct = r[key];
    if (direct && typeof direct === 'object') return direct;

    // Handle alternate field names across collections
    const aliases = {
      'EV sales count': ['EV', 'EV Sales', 'EV_Sales'],
      'ICE sales count': ['ICE', 'ICE Sales', 'ICE_Sales'],
      'EV%': ['EV %', 'EV Percentage', 'EV_PCT'],
      'ICE%': ['ICE %', 'ICE Percentage', 'ICE_PCT'],
      'EV Penetration rate': ['EV Pentration Rate in %', 'EV Penetration Rate', 'EV_Penetration_Rate'],
      'ICE Penetration rate': ['ICE Pentration Rate in %', 'ICE Penetration Rate', 'ICE_Penetration_Rate'],
    };
    const candidates = aliases[key] || [];
    for (const k of candidates) {
      const v = r[k];
      if (v && typeof v === 'object') return v;
    }
    return {};
  };

  /** Sales-weighted average of a per-row penetration series for one year (values as stored in MongoDB). */
  const weightedAvgPenetrationFromDb = (rows, year, penKey) => {
    let num = 0;
    let den = 0;
    (rows || []).forEach((r) => {
      const p = toNumOrNull(getSeries(r, penKey)[year]);
      if (p == null) return;
      const ev = toNum(getSeries(r, 'EV sales count')[year]);
      const ice = toNum(getSeries(r, 'ICE sales count')[year]);
      const w = ev + ice;
      if (w <= 0) return;
      num += p * w;
      den += w;
    });
    return den > 0 ? num / den : null;
  };

  const getYearsFromRows = (rs) => {
    const ys = new Set();
    (rs || []).forEach((r) => {
      Object.keys(getSeries(r, 'EV%')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'ICE%')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'EV sales count')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'ICE sales count')).forEach((k) => ys.add(k));
    });
    return [...ys]
      .map((y) => parseInt(String(y), 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
      .map(String);
  };

  /** Year keys that exist on EV / ICE sales count objects for these rows only (aligned with MongoDB docs). */
  const getSalesCountYearsFromRows = (rows) => {
    const ys = new Set();
    (rows || []).forEach((r) => {
      Object.keys(getSeries(r, 'EV sales count')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'ICE sales count')).forEach((k) => ys.add(k));
    });
    return [...ys]
      .map((y) => parseInt(String(y), 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
      .map(String);
  };

  const stateYears = useMemo(() => getYearsFromRows(stateRows), [stateRows]);
  const cityYears = useMemo(() => getYearsFromRows(cityRows), [cityRows]);
  const rtoYears = useMemo(() => getYearsFromRows(rtoRows), [rtoRows]);

  const getSelectedYears = (ys) => ys;

  const latestYearFor = (ys) => {
    const selected = getSelectedYears(ys);
    return selected.length ? selected[selected.length - 1] : (ys[ys.length - 1] || '');
  };

  const buildStats = (dim, rs, ys) => {
    const latestYear = latestYearFor(ys);
    if (!rs.length || !latestYear) {
      return { total: rs.length, avgEv: null, top: null, maxEv: null, latestYear };
    }
    const evPctByRow = rs.map((r) => {
      const evPct = Number(getSeries(r, 'EV%')[latestYear]);
      return { name: getLabel(dim, r), evPct: Number.isFinite(evPct) ? evPct : null };
    }).filter((x) => x.evPct != null);
    const total = rs.length;
    const avgEv = evPctByRow.length ? (evPctByRow.reduce((s, x) => s + x.evPct, 0) / evPctByRow.length) : null;
    const top = evPctByRow.length
      ? evPctByRow.reduce((best, x) => (best == null || x.evPct > best.evPct ? x : best), null)
      : null;
    const maxEv = top ? top.evPct : null;
    return { total, avgEv, top: top ? top.name : null, maxEv, latestYear };
  };

  const stateStats = useMemo(() => buildStats('state', stateRows, stateYears), [stateRows, stateYears]);
  const cityStats = useMemo(() => buildStats('city', cityRows, cityYears), [cityRows, cityYears]);
  const rtoStats = useMemo(() => buildStats('rto', rtoRows, rtoYears), [rtoRows, rtoYears]);

  const accentColor = theme.palette.info.main;

  const globalYears = useMemo(() => {
    const ys = new Set();
    [...stateRows, ...cityRows, ...rtoRows].forEach((r) => {
      Object.keys(getSeries(r, 'EV%')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'ICE%')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'EV sales count')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'ICE sales count')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'EV Penetration rate')).forEach((k) => ys.add(k));
      Object.keys(getSeries(r, 'ICE Penetration rate')).forEach((k) => ys.add(k));
    });
    return [...ys]
      .map((y) => parseInt(String(y), 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
      .map(String);
  }, [stateRows, cityRows, rtoRows]);

  const yearOptions = useMemo(() => globalYears.filter((y) => y !== '2022'), [globalYears]);

  const [citySnapshotYear, setCitySnapshotYear] = useState('');
  useEffect(() => {
    if (!yearOptions.length) return;
    setCitySnapshotYear((prev) => (prev && yearOptions.includes(prev) ? prev : yearOptions[yearOptions.length - 1]));
  }, [yearOptions]);

  const yearlyPenetrationComparison = useMemo(() => {
    const years = globalYears;
    if (!years.length) return null;

    // Prefer state-level totals (least double-counting risk).
    const src = (stateRows && stateRows.length) ? stateRows : (rtoRows || []);
    if (!src.length) return null;

    const totals = years.map((y) => {
      const ev = src.reduce((s, r) => s + toNum(getSeries(r, 'EV sales count')[y]), 0);
      const ice = src.reduce((s, r) => s + toNum(getSeries(r, 'ICE sales count')[y]), 0);
      const denom = ev + ice;
      const evPct = denom ? (ev / denom) * 100 : 0;
      const icePct = denom ? (ice / denom) * 100 : 0;
      return { y, evPct, icePct };
    });

    return {
      labels: totals.map((t) => t.y),
      datasets: [
        {
          label: 'EV%',
          data: totals.map((t) => Number(t.evPct.toFixed(2))),
          borderColor: theme.palette.info.main,
          backgroundColor: alpha(theme.palette.info.main, 0.12),
          pointRadius: 3,
          tension: 0.25,
        },
        {
          label: 'ICE%',
          data: totals.map((t) => Number(t.icePct.toFixed(2))),
          borderColor: theme.palette.error.main,
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          pointRadius: 3,
          tension: 0.25,
        },
      ],
    };
  }, [globalYears, stateRows, rtoRows, theme]);

  // For State: top 1 per year (few rows, no volume filter needed)
  const buildTop1PerYear = (dim, rs) => {
    if (!globalYears.length) return null;
    const tops = globalYears.map((y) => {
      let best = null;
      for (const r of rs) {
        const v = toNumOrNull(getSeries(r, 'EV%')[y]);
        if (v == null) continue;
        if (!best || v > best.evPct) best = { label: getLabel(dim, r), evPct: v };
      }
      return { year: y, label: best?.label || '—', evPct: best?.evPct ?? null };
    });
    return {
      labels: tops.map((t) => t.year),
      datasets: [{
        label: 'Top EV% (value)',
        data: tops.map((t) => (t.evPct == null ? null : Number(t.evPct.toFixed(2)))),
        backgroundColor: alpha(theme.palette.info.main, 0.6),
        borderColor: theme.palette.info.main,
        borderWidth: 1,
      }],
      __topLabels: tops.map((t) => t.label),
    };
  };

  const topStateEachYear = useMemo(() => buildTop1PerYear('state', stateRows), [stateRows, globalYears, theme]);

  // Top states: one line per state. Plotted Y values are read directly from each MongoDB document’s
  // nested year maps (EV%, ICE%, EV/ICE Penetration rate) — no client-side recomputation from counts.
  // Only: choose top 10 states by latest-year EV% and min total sales (EV+ICE).
  const topStatesYoYByMetric = useMemo(() => {
    if (!globalYears.length || !stateRows.length) return null;
    const latestY = globalYears[globalYears.length - 1];
    const MIN_TOTAL = 200;

    const eligible = stateRows.map((r) => {
      const evPctLatest = toNumOrNull(getSeries(r, 'EV%')[latestY]);
      const evLatest = toNum(getSeries(r, 'EV sales count')[latestY]);
      const iceLatest = toNum(getSeries(r, 'ICE sales count')[latestY]);
      if ((evLatest + iceLatest) < MIN_TOTAL) return null;
      if (evPctLatest == null) return null;
      return { r, label: getLabel('state', r), evPctLatest };
    }).filter(Boolean)
      .sort((a, b) => b.evPctLatest - a.evPctLatest)
      .slice(0, 10);

    if (!eligible.length) return null;

    const palette = [
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.success?.main || '#2e7d32',
      theme.palette.warning?.main || '#ed6c02',
      '#9c27b0', '#009688', '#795548', '#607d8b',
    ];

    const seriesKeys = {
      evPct: 'EV%',
      icePct: 'ICE%',
      evPen: 'EV Penetration rate',
      icePen: 'ICE Penetration rate',
    };

    const build = (metric) => {
      const key = seriesKeys[metric];
      return {
        labels: globalYears,
        datasets: eligible.map((c, idx) => ({
          label: c.label,
          data: globalYears.map((y) => {
            const v = toNumOrNull(getSeries(c.r, key)[y]);
            return v == null ? null : Number(v.toFixed(2));
          }),
          borderColor: palette[idx % palette.length],
          backgroundColor: alpha(palette[idx % palette.length], 0.1),
          pointRadius: 3,
          tension: 0.25,
        })),
      };
    };

    return {
      evPct: build('evPct'),
      icePct: build('icePct'),
      evPen: build('evPen'),
      icePen: build('icePen'),
    };
  }, [globalYears, stateRows, theme]);

  // No client-side growth formula: rank cities by stored EV Penetration rate for the selected year.
  const topCitiesEvPenetrationRateFromDb = useMemo(() => {
    if (!citySnapshotYear || !cityRows.length) return null;
    const y = citySnapshotYear;
    const scored = cityRows
      .map((r) => {
        const v = toNumOrNull(getSeries(r, 'EV Penetration rate')[y]);
        if (v == null) return null;
        return { label: getLabel('city', r), v };
      })
      .filter(Boolean)
      .sort((a, b) => b.v - a.v)
      .slice(0, 10);
    if (!scored.length) return null;
    return {
      labels: scored.map((s) => s.label),
      datasets: [{
        label: `EV Penetration rate (${y})`,
        data: scored.map((s) => Number(s.v.toFixed(2))),
        backgroundColor: alpha(theme.palette.warning.main, 0.6),
        borderColor: theme.palette.warning.main,
        borderWidth: 1,
      }],
    };
  }, [cityRows, citySnapshotYear, theme]);

  const buildTop10 = (dim, rs, ys) => {
    const yearList = (globalYears && globalYears.length) ? globalYears : ys;
    const latestYear = yearList.length ? yearList[yearList.length - 1] : latestYearFor(ys);
    if (!latestYear) return null;

    // Rank by overall EV% across all available years (highest to lowest).
    const top = rs
      .map((r) => {
        const values = yearList
          .map((y) => toNumOrNull(getSeries(r, 'EV%')[y]))
          .filter((v) => v != null);
        if (!values.length) return null;
        const overallEvPct = values.reduce((s, v) => s + v, 0) / values.length;
        return { label: getLabel(dim, r), overallEvPct, row: r };
      })
      .filter(Boolean)
      .sort((a, b) => b.overallEvPct - a.overallEvPct)
      .slice(0, 10);

    if (!top.length) return null;

    const palette = [
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success?.main || theme.palette.info.dark,
      theme.palette.warning?.main || theme.palette.info.light,
    ];

    return {
      labels: top.map((t) => t.label),
      datasets: yearList.map((y, idx) => ({
        label: `EV% (${y})`,
        data: top.map((t) => {
          // Keep nulls as null (don't force to 0)
          return toNumOrNull(getSeries(t.row, 'EV%')[y]);
        }),
        backgroundColor: alpha(palette[idx % palette.length], 0.55),
        borderColor: palette[idx % palette.length],
        borderWidth: 1,
      })),
    };
  };

  const stateTop10 = useMemo(() => buildTop10('state', stateRows, stateYears), [stateRows, stateYears, theme, globalYears]);
  const cityTop10 = useMemo(() => buildTop10('city', cityRows, cityYears), [cityRows, cityYears, theme, globalYears]);
  const rtoTop10 = useMemo(() => buildTop10('rto', rtoRows, rtoYears), [rtoRows, rtoYears, theme, globalYears]);

  const renderSection = (dim, rs, ys, top10, st) => {
    const meta = DIM_META[dim];
    const penetrationCollection =
      dim === 'state' ? 'state_penetration' : dim === 'city' ? 'city_penetration' : 'rto_penetration';
    const sourceRows = dim === 'state' ? stateRows : rs;
    const salesCountYears = getSalesCountYearsFromRows(sourceRows);
    const selectedYears = salesCountYears.length
      ? salesCountYears
      : (globalYears.length ? globalYears : getSelectedYears(ys));
    const latestYear = selectedYears.length ? selectedYears[selectedYears.length - 1] : (st.latestYear || latestYearFor(ys));
    const sectionAccent = dim === 'state'
      ? theme.palette.primary.main
      : dim === 'city'
        ? theme.palette.secondary.main
        : accentColor;

    // Exact (non-average) series built from totals:
    // Sum EV/ICE counts across all rows for each year, then derive EV%/ICE% and penetration rates.
    const totals = (() => {
      const evTotals = selectedYears.map((y) =>
        sourceRows.reduce((s, r) => s + toNum(getSeries(r, 'EV sales count')[y]), 0)
      );
      const iceTotals = selectedYears.map((y) =>
        sourceRows.reduce((s, r) => s + toNum(getSeries(r, 'ICE sales count')[y]), 0)
      );
      const evPct = evTotals.map((ev, i) => {
        const denom = ev + (iceTotals[i] || 0);
        return denom ? (ev / denom) * 100 : null;
      });
      const icePct = iceTotals.map((ice, i) => {
        const denom = (evTotals[i] || 0) + ice;
        return denom ? (ice / denom) * 100 : null;
      });
      const evRate = selectedYears.map((y) => weightedAvgPenetrationFromDb(sourceRows, y, 'EV Penetration rate'));
      const iceRate = selectedYears.map((y) => weightedAvgPenetrationFromDb(sourceRows, y, 'ICE Penetration rate'));
      return { evTotals, iceTotals, evPct, icePct, evRate, iceRate };
    })();

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {dim === 'state' ? <Box /> : (
            <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: '0.01em' }}>
              {meta.label} penetration
            </Typography>
          )}
          {dim !== 'state' && (
            <Chip
              label={`${meta.titlePlural}: ${st.total || 0}`}
              size="small"
              sx={{
                backgroundColor: alpha(sectionAccent, 0.1),
                color: sectionAccent,
                fontWeight: 800,
                border: `1px solid ${alpha(sectionAccent, 0.25)}`,
              }}
            />
          )}
        </Box>

        <Grid container spacing={2}>
          {top10 && (
            <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
              <ChartContainer
                title={`Top 10 ${meta.titlePlural} by EV% — (${penetrationCollection})`}
                isLoading={isLoading}
                chartRef={null}
                chartId={`penetration-${dim}-top10`}
                chartType="bar"
                height="auto"
                sx={{ flex: 1 }}
              >
                <Box sx={{ height: 320 }}>
                  <Bar data={top10} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'top' },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const v = ctx.raw;
                            if (v == null || v === '') return `${ctx.dataset.label}: —`;
                            return `${ctx.dataset.label}: ${Number(v).toLocaleString()}%`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` }, grid: { color: alpha(theme.palette.divider, 0.35) } },
                      x: { grid: { display: false } },
                    },
                  }} />
                </Box>
              </ChartContainer>
            </Grid>
          )}

          {dim !== 'state' && (
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <ChartContainer
                title={`EV% vs ICE% trend — ${meta.label} (summed EV/ICE counts, ${penetrationCollection})`}
                isLoading={isLoading}
                chartRef={null}
                chartId={`penetration-${dim}-evpct-trend`}
                chartType="line"
                height="auto"
                sx={{ flex: 1 }}
              >
                <Box sx={{ height: 320 }}>
                  <Line
                    data={{
                      labels: selectedYears,
                      datasets: [
                        {
                          label: 'EV%',
                          data: totals.evPct.map((v) => (v == null ? null : Number(v.toFixed(2)))),
                          borderColor: theme.palette.info.main,
                          backgroundColor: alpha(theme.palette.info.main, 0.12),
                          pointRadius: 3,
                          tension: 0.25,
                        },
                        {
                          label: 'ICE%',
                          data: totals.icePct.map((v) => (v == null ? null : Number(v.toFixed(2)))),
                          borderColor: theme.palette.error.main,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          pointRadius: 3,
                          tension: 0.25,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: true, position: 'top' } },
                      scales: {
                        y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` }, grid: { color: alpha(theme.palette.divider, 0.35) } },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
          )}

          {dim !== 'state' && (
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <ChartContainer
                title={`EV vs ICE sales — ${meta.label} (summed counts, ${penetrationCollection})`}
                isLoading={isLoading}
                chartRef={null}
                chartId={`penetration-${dim}-sales-trend`}
                chartType="line"
                height="auto"
                sx={{ flex: 1 }}
              >
                <Box sx={{ height: 320 }}>
                  <Line
                    data={{
                      labels: selectedYears,
                      datasets: [
                        {
                          label: 'EV sales',
                          data: totals.evTotals,
                          borderColor: theme.palette.info.main,
                          backgroundColor: alpha(theme.palette.info.main, 0.12),
                          pointRadius: 3,
                          tension: 0.25,
                        },
                        {
                          label: 'ICE sales',
                          data: totals.iceTotals,
                          borderColor: theme.palette.error.main,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          pointRadius: 3,
                          tension: 0.25,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true, position: 'top' },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              const v = ctx.raw;
                              if (v == null || v === '') return `${ctx.dataset.label}: —`;
                              return `${ctx.dataset.label}: ${Number(v).toLocaleString()}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: { beginAtZero: true, ticks: { callback: (v) => Number(v).toLocaleString() }, grid: { color: alpha(theme.palette.divider, 0.35) } },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
          )}

          {/* Penetration rate trend chart removed per request */}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      }}>
        <Box sx={{
          px: 2, pt: 1, pb: 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5,
        }}>
          <Typography variant="h6" fontWeight={900} fontSize="1.15rem">
            Penetration Analytics
          </Typography>
        </Box>
      </Box>

      <Box ref={contentRef} sx={{ px: 2, pt: 2, pb: 4 }}>
        {yearlyPenetrationComparison && (
          <Box sx={{ mb: 3 }}>
            <ChartContainer
              title="EV Market Share Trend (EV% vs ICE%) — All India"
              isLoading={isLoading}
              chartRef={null}
              chartId="penetration-allindia-yearly-penetration"
              chartType="line"
              height="auto"
              sx={{ flex: 1 }}
            >
              <Box sx={{ height: 340 }}>
                <Line
                  data={yearlyPenetrationComparison}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true, position: 'top' },
                      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.raw || 0)}%` } },
                    },
                    scales: {
                      y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` }, grid: { color: alpha(theme.palette.divider, 0.35) } },
                      x: { grid: { display: false } },
                    },
                  }}
                />
              </Box>
            </ChartContainer>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {topStateEachYear && (
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <ChartContainer
                title="Top State each year — EV%"
                isLoading={isLoading}
                chartRef={null}
                chartId="penetration-top-rising-states"
                chartType="bar"
                height="auto"
                sx={{ flex: 1 }}
              >
                <Box sx={{ height: 320 }}>
                  <Bar
                    data={{
                      labels: topStateEachYear.labels,
                      datasets: topStateEachYear.datasets,
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true, position: 'top' },
                        tooltip: {
                          callbacks: {
                            title: (items) => {
                              const idx = items?.[0]?.dataIndex ?? 0;
                              const name = topStateEachYear.__topLabels?.[idx] || '—';
                              return `${topStateEachYear.labels[idx]} — ${name}`;
                            },
                            label: (ctx) => `EV%: ${ctx.raw}%`,
                          },
                        },
                      },
                      scales: {
                        y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` }, grid: { color: alpha(theme.palette.divider, 0.35) } },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
          )}

          {topStatesYoYByMetric && (
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <ChartContainer
                title="Top 10 states — YoY"
                isLoading={isLoading}
                chartRef={null}
                chartId="penetration-states-yoy-by-state"
                chartType="line"
                height="auto"
                sx={{ flex: 1 }}
              >
                <Box sx={{ px: 1, pb: 1 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={stateYoYMetric}
                    onChange={(_, v) => {
                      if (v != null) setStateYoYMetric(v);
                    }}
                    sx={{ flexWrap: 'wrap', mb: 1 }}
                  >
                    <ToggleButton value="evPct">{STATE_YOY_METRIC_LABELS.evPct}</ToggleButton>
                    <ToggleButton value="icePct">{STATE_YOY_METRIC_LABELS.icePct}</ToggleButton>
                    <ToggleButton value="evPen">{STATE_YOY_METRIC_LABELS.evPen}</ToggleButton>
                    <ToggleButton value="icePen">{STATE_YOY_METRIC_LABELS.icePen}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ height: 360 }}>
                  <Line
                    data={topStatesYoYByMetric[stateYoYMetric]}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: {
                        legend: { display: true, position: 'top' },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => {
                              const v = ctx.raw;
                              if (v == null) return `${ctx.dataset.label}: —`;
                              return `${ctx.dataset.label}: ${v}%`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: stateYoYMetric === 'evPct' || stateYoYMetric === 'icePct',
                          title: {
                            display: true,
                            text:
                              stateYoYMetric === 'evPen' || stateYoYMetric === 'icePen'
                                ? 'Penetration rate % (YoY)'
                                : 'Share %',
                          },
                          ticks: { callback: (v) => `${v}%` },
                          grid: { color: alpha(theme.palette.divider, 0.35) },
                        },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
          )}

        </Grid>

        {renderSection('state', stateRows, stateYears, stateTop10, stateStats)}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {topCitiesEvPenetrationRateFromDb && (
            <Grid item xs={12} md={12} sx={{ display: 'flex' }}>
              <ChartContainer
                title={`Top 10 Cities — EV Penetration rate (${citySnapshotYear || '—'})`}
                isLoading={isLoading}
                chartRef={null}
                chartId="penetration-city-ev-penetration-rate-db"
                chartType="bar"
                height="auto"
                sx={{ flex: 1 }}
              >
                {yearOptions.length > 0 && (
                  <Box sx={{ px: 1, pt: 1, pb: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Year:
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      value={citySnapshotYear}
                      onChange={(_, v) => {
                        if (v != null) setCitySnapshotYear(v);
                      }}
                      size="small"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {yearOptions.map((y) => (
                        <ToggleButton key={y} value={y} sx={{ textTransform: 'none' }}>
                          {y}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                )}
                <Box sx={{ height: 320 }}>
                  <Bar
                    data={topCitiesEvPenetrationRateFromDb}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true, position: 'top' },
                        tooltip: {
                          callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`,
                          },
                        },
                      },
                      scales: {
                        y: { ticks: { callback: (v) => `${v}%` }, grid: { color: alpha(theme.palette.divider, 0.35) } },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                </Box>
              </ChartContainer>
            </Grid>
          )}

          {citySnapshotYear && cityRows.length > 0 && !topCitiesEvPenetrationRateFromDb && !isLoading && (
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No cities with a stored EV Penetration rate for <strong>{citySnapshotYear}</strong>. Try another year.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default PenetrationAnalytics;

